import React, { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  loginCustomer,
  verifyOtpMethod,
  resendOtpMethod,
} from "../../../api/authApi";
import gsLogo from "../../../assets/gs_logo-goktas.svg";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const getResponseData = (response) => {
  return response?.data?.data ?? response?.data ?? null;
};

const getResponseToken = (data) => {
  if (!data) return null;
  if (typeof data === "string") return data;

  return (
    data.token ||
    data.accessToken ||
    (typeof data.data === "string" ? data.data : null) ||
    data.data?.token ||
    null
  );
};

const getErrorMessage = (error, fallbackMessage) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.msg ||
    error?.message ||
    fallbackMessage
  );
};

function Login() {
  // Form Verileri
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // OTP Verileri
  const [step, setStep] = useState("LOGIN"); // 'LOGIN' | 'OTP'
  const [otpCode, setOtpCode] = useState("");
  const [otpToken, setOtpToken] = useState(null);

  // Deneme Hakkı ve Timer
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [timer, setTimer] = useState(0);

  // UI Durumları
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Yönlendirme Kontrolü (Gecikmeli)
  useEffect(() => {
    if (pendingRedirect && user) {
      const timeout = setTimeout(() => {
        setModalOpen(false);
        navigate("/dashboard");
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [pendingRedirect, user, navigate]);

  // Sayaç Mantığı
  useEffect(() => {
    let interval;
    if (step === "OTP" && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // Tekrar Kod Gönderme
  const handleResendOtp = async () => {
    if (timer > 0 || resendLoading || !otpToken) return;

    setResendLoading(true);

    try {
      const response = await resendOtpMethod(otpToken);
      const data = getResponseData(response);

      setTimer(data?.expiresInSec ?? 120);
      setAttemptsLeft(data?.attemptsLeft ?? 3);
      setOtpCode("");

      setModalMessage(
        data?.message || "Yeni kod e-posta adresinize gönderildi."
      );
      setModalError(false);
      setModalOpen(true);
    } catch (err) {
      console.error("Resend Error:", err);

      const errorData = err?.response?.data;

      if (typeof errorData?.remainingSec === "number") {
        setTimer(errorData.remainingSec);
      }

      if (errorData?.code === "OTP_LOCKED") {
        setAttemptsLeft(0);
      }

      setModalMessage(getErrorMessage(err, "Kod gönderilemedi."));
      setModalError(true);
      setModalOpen(true);
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setLoading(true);

    try {
      if (step === "LOGIN") {
        // ADIM 1: E-posta ve şifre kontrolü
        const response = await loginCustomer(email.trim(), password);
        const data = getResponseData(response);

        /*
         * otpStatus true veya alan yok:
         * {
         *   step: "OTP_REQUIRED",
         *   otpRequired: true,
         *   otpToken: "...",
         *   expiresInSec: 120,
         *   attemptsLeft: 3
         * }
         *
         * otpStatus false:
         * {
         *   step: "LOGIN_SUCCESS",
         *   otpRequired: false,
         *   token: "..."
         * }
         */
        if (data?.step === "OTP_REQUIRED" || data?.otpRequired === true) {
          if (!data?.otpToken) {
            throw new Error("OTP oturum tokenı bulunamadı.");
          }

          setOtpToken(data.otpToken);
          setOtpCode("");
          setStep("OTP");
          setTimer(data.expiresInSec ?? 120);
          setAttemptsLeft(data.attemptsLeft ?? 3);

          setModalMessage(
            data?.message ||
              "E-posta adresinize gönderilen doğrulama kodunu giriniz."
          );
          setModalError(false);
          setModalOpen(true);
          return;
        }

        // otpStatus false ise backend doğrudan JWT döndürür.
        const finalToken = getResponseToken(data);

        if (!finalToken) {
          throw new Error("Giriş tokenı bulunamadı.");
        }

        login(finalToken);
        setModalMessage("Giriş başarılı! Yönlendiriliyorsunuz...");
        setModalError(false);
        setModalOpen(true);
        setPendingRedirect(true);
        return;
      }

      // ADIM 2: OTP doğrulama
      if (!otpToken) {
        throw new Error("OTP oturumu bulunamadı. Lütfen tekrar giriş yapın.");
      }

      if (!/^\d{6}$/.test(otpCode)) {
        throw new Error("Doğrulama kodu 6 haneli olmalıdır.");
      }

      const response = await verifyOtpMethod(otpToken, otpCode);
      const data = getResponseData(response);
      const finalToken = getResponseToken(data);

      if (!finalToken) {
        throw new Error("Giriş tokenı bulunamadı.");
      }

      login(finalToken);
      setModalMessage("Giriş başarılı! Yönlendiriliyorsunuz...");
      setModalError(false);
      setModalOpen(true);
      setPendingRedirect(true);
    } catch (err) {
      const errorData = err?.response?.data;
      const errorCode = errorData?.code;

      if (step === "OTP") {
        if (typeof errorData?.attemptsLeft === "number") {
          setAttemptsLeft(errorData.attemptsLeft);
        } else if (errorCode === "OTP_LOCKED") {
          setAttemptsLeft(0);
        } else if (errorCode === "OTP_INVALID") {
          setAttemptsLeft((prev) => Math.max(0, prev - 1));
        }

        if (
          errorCode === "OTP_EXPIRED" ||
          errorCode === "OTP_TOKEN_EXPIRED" ||
          errorCode === "OTP_TOKEN_INVALID"
        ) {
          setTimer(0);
        }
      }

      setModalMessage(
        getErrorMessage(
          err,
          step === "LOGIN"
            ? "Giriş başarısız!"
            : "Hatalı veya süresi dolmuş kod!"
        )
      );
      setModalError(true);
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep("LOGIN");
    setOtpCode("");
    setOtpToken(null);
    setTimer(0);
    setAttemptsLeft(3);
    setPendingRedirect(false);
    setModalOpen(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f2f3f7 65%, #e3e4e8 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: 370,
          background: "#fff",
          padding: 36,
          borderRadius: 18,
          boxShadow: "0 6px 38px #d0d3db70",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          border: "1.5px solid #edeef3",
        }}
      >
        <div
          style={{
            width: 104,
            height: 104,
            borderRadius: "50%",
            background: "#e6e8ec",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 18,
            boxShadow: "0 2px 12px #dbe2ee66",
          }}
        >
          <img
            src={gsLogo}
            alt="Logo"
            style={{ width: 64, height: 64, display: "block" }}
          />
        </div>

        <h1
          style={{
            textAlign: "center",
            marginBottom: 26,
            color: "#25304a",
            letterSpacing: ".01em",
            fontWeight: 600,
          }}
        >
          Göktaş Metal
        </h1>

        <h2
          style={{
            textAlign: "center",
            marginBottom: 26,
            color: "#25304a",
            letterSpacing: ".01em",
            fontWeight: 600,
          }}
        >
          {step === "LOGIN" ? "Giriş Yap" : "OTP Doğrulama"}
        </h2>

        {step === "LOGIN" && (
          <>
            <label
              style={{
                color: "#444",
                width: "100%",
                marginBottom: 4,
                fontWeight: 500,
              }}
            >
              E-Posta
            </label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              style={{
                width: "100%",
                marginBottom: 14,
                padding: 11,
                borderRadius: 8,
                border: "1.5px solid #e4e8ec",
                background: "#f7f8fa",
                color: "#222",
                fontSize: 16,
                outline: "none",
                transition: "border .2s",
              }}
            />
            <label
              style={{
                color: "#444",
                width: "100%",
                marginBottom: 4,
                fontWeight: 500,
              }}
            >
              Şifre
            </label>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              required
              style={{
                width: "100%",
                marginBottom: 18,
                padding: 11,
                borderRadius: 8,
                border: "1.5px solid #e4e8ec",
                background: "#f7f8fa",
                color: "#222",
                fontSize: 16,
                outline: "none",
                transition: "border .2s",
              }}
            />
          </>
        )}

        {step === "OTP" && (
          <>
            <div
              style={{
                width: "100%",
                marginBottom: 14,
                textAlign: "center",
                fontSize: 14,
                color: "#666",
              }}
            >
              Lütfen <strong>{email}</strong> adresine gönderilen kodu giriniz.
            </div>
            <label
              style={{
                color: "#444",
                width: "100%",
                marginBottom: 4,
                fontWeight: 500,
              }}
            >
              Doğrulama Kodu
            </label>
            <input
              value={otpCode}
              onChange={(e) =>
                setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              maxLength={6}
              required
              autoFocus
              disabled={attemptsLeft <= 0}
              style={{
                width: "100%",
                marginBottom: 8,
                padding: 11,
                borderRadius: 8,
                border: `1.5px solid ${
                  attemptsLeft === 0 ? "#e34848" : "#e4e8ec"
                }`,
                background: attemptsLeft === 0 ? "#fff0f0" : "#f7f8fa",
                color: "#222",
                fontSize: 16,
                outline: "none",
                transition: "border .2s",
                letterSpacing: "4px",
                textAlign: "center",
                fontWeight: "bold",
              }}
            />
            <div
              style={{
                width: "100%",
                textAlign: "center",
                marginBottom: 18,
                fontSize: 13,
                fontWeight: 500,
                color: attemptsLeft <= 1 ? "#e34848" : "#777",
              }}
            >
              {attemptsLeft > 0
                ? `Kalan Deneme Hakkı: ${attemptsLeft}`
                : "Deneme hakkınız bitti. Lütfen kodu tekrar isteyin."}
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={
            loading ||
            (step === "OTP" && (attemptsLeft <= 0 || otpCode.length !== 6))
          }
          style={{
            width: "100%",
            padding: 12,
            background:
              loading ||
              (step === "OTP" && (attemptsLeft <= 0 || otpCode.length !== 6))
                ? "#d0d3db"
                : "linear-gradient(90deg, #e7eaf0 10%, #f3f4f9 90%)",
            color:
              loading ||
              (step === "OTP" && (attemptsLeft <= 0 || otpCode.length !== 6))
                ? "#888"
                : "#25304a",
            borderRadius: 8,
            border: "none",
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: ".02em",
            marginTop: 6,
            boxShadow: "0 2px 8px #e6e8ea40",
            transition: "background .2s",
            cursor:
              loading ||
              (step === "OTP" && (attemptsLeft <= 0 || otpCode.length !== 6))
                ? "not-allowed"
                : "pointer",
          }}
        >
          {loading ? "İşleniyor..." : step === "LOGIN" ? "Giriş" : "Doğrula"}
        </button>

        {step === "OTP" && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginTop: 16,
              gap: 8,
            }}
          >
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={timer > 0 || resendLoading}
              style={{
                background: "transparent",
                border: "none",
                color: timer > 0 || resendLoading ? "#999" : "#3949ab",
                cursor: timer > 0 || resendLoading ? "default" : "pointer",
                fontWeight: timer > 0 || resendLoading ? "normal" : "bold",
                fontSize: 14,
                textDecoration:
                  timer > 0 || resendLoading ? "none" : "underline",
              }}
            >
              {resendLoading
                ? "Kod gönderiliyor..."
                : timer > 0
                  ? `Tekrar kod göndermek için: ${Math.floor(timer / 60)}:${(
                      timer % 60
                    )
                      .toString()
                      .padStart(2, "0")}`
                  : "Kodu Tekrar Gönder"}
            </button>
            <button
              type="button"
              onClick={handleBackToLogin}
              style={{
                background: "transparent",
                border: "none",
                color: "#666",
                cursor: "pointer",
                fontSize: 14,
                textDecoration: "underline",
              }}
            >
              Geri Dön
            </button>
          </div>
        )}
      </form>

      {/* --- MODAL --- */}
      <Dialog.Root open={modalOpen} onOpenChange={setModalOpen}>
        <Dialog.Portal>
          {/* Overlay Z-Index Yükseltildi ve Rengi Koyulaştırıldı */}
          <Dialog.Overlay
            style={{
              background: "rgba(0,0,0,0.6)",
              position: "fixed",
              inset: 0,
              zIndex: 99999, // Çok yüksek Z-Index
              pointerEvents: "auto",
            }}
          />
          {/* Content Z-Index Yükseltildi */}
          <Dialog.Content
            style={{
              background: "#f7f8fa",
              borderRadius: 12,
              padding: 18,
              minWidth: 220,
              maxWidth: 340,
              border: `2px solid ${modalError ? "#c62828" : "#3949ab"}`,
              boxShadow: "0 4px 32px #dadfe870",
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              zIndex: 100000, // Çok yüksek Z-Index
              pointerEvents: "auto",
              color: "#222",
            }}
          >
            <Dialog.Title
              style={{
                fontWeight: 700,
                fontSize: 20,
                color: modalError ? "#e34848" : "#3949ab",
                marginBottom: 14,
                letterSpacing: ".01em",
              }}
            >
              {modalMessage}
            </Dialog.Title>
            <button
              onClick={() => setModalOpen(false)}
              style={{
                width: "100%",
                padding: 10,
                marginTop: 8,
                background: modalError ? "#e34848" : "#3949ab",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                fontWeight: "bold",
                fontSize: 16,
                cursor: "pointer",
              }}
            >
              Kapat
            </button>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

export default Login;
